import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { SIMULATION_ENDPOINT_PREFIX } from 'routes/HumanResourcesModule/Simulation/helpers/constants';
import {
    FreeIFSEHypothesisInformation,
    FreeFilters,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/types';
import { RQAPICall, RQAPICallWithData } from 'store/APIClient';
import { filterFilters } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/dataHelpers';

// Retrieve the following lists, required to create an IFSE hypothesis : cadre d'emplois, fonctions, filiere and catégories
const IFSE_INFORMATION_API_KEY = 'IFSE_information';
export const useGetIFSEInformation = (
    hrSimulationId: number,
): UseQueryResult<FreeIFSEHypothesisInformation> => {
    const endpoint = `${SIMULATION_ENDPOINT_PREFIX}/${hrSimulationId}/ifse_information`;

    return useQuery({
        queryKey: [IFSE_INFORMATION_API_KEY, hrSimulationId],
        queryFn: async () => {
            const getResponse = await RQAPICall({
                endpoint,
                method: 'GET',
            });
            return getResponse.content.data;
        },
        meta: {
            useGlobalOnError: true,
        },
    });
};
const IFSE_AGENTS_NUMBER_API_KEY = 'IFSE_agents_number";';
type IFSECount = {
    uniqueAgentSoftwareIds: string[];
};
export const usePostIFSEInformation = ({
    filters,
    simulationId,
    hypothesisId,
}: {
    filters: FreeFilters;
    simulationId: number;
    hypothesisId?: number;
}): UseQueryResult<IFSECount> => {
    const endpoint = `${SIMULATION_ENDPOINT_PREFIX}/${simulationId}/ifse_information`;
    return useQuery({
        queryKey: [
            IFSE_AGENTS_NUMBER_API_KEY,
            simulationId,
            JSON.stringify(filters),
        ],
        queryFn: async () => {
            const hypoObj = hypothesisId !== undefined ? { hypothesisId } : {};
            const response = await RQAPICall({
                endpoint,
                method: 'POST',
                body: {
                    filters: filterFilters(filters),
                    ...hypoObj,
                },
            });
            return response.content.data;
        },
        meta: {
            useGlobalOnError: true,
        },
    });
};

type AgentsByIFSEHypothesis = {
    [hypId: string]: string[];
};

/**
 * Hook to get the list of all agents software ids that are affected by IFSE FreeHypotheses.
 */
export const usePostIFSEInformationMany = ({
    simulationId,
    hypotheses,
    enabled = true,
}: {
    simulationId: number;
    hypotheses: {
        filters: FreeFilters;
        id: number;
    }[];
    enabled: boolean;
}): UseQueryResult<AgentsByIFSEHypothesis> => {
    const endpoint = `${SIMULATION_ENDPOINT_PREFIX}/${simulationId}/ifse_information`;
    return useQuery({
        queryKey: [IFSE_AGENTS_NUMBER_API_KEY, simulationId],
        queryFn: async () => {
            // The endpoint returns the list of agents software ids affected by a single hypothesis.
            // We have to call it as many times as there are hypotheses and aggregate the data.
            const hypothesesInfo = await Promise.all(
                hypotheses.map(async (hyp) => {
                    const response = await RQAPICallWithData<IFSECount>({
                        endpoint,
                        method: 'POST',
                        body: {
                            filters: filterFilters(hyp.filters),
                            hypothesisId: hyp.id,
                        },
                    });
                    return [
                        hyp.id,
                        response.content.data.uniqueAgentSoftwareIds,
                    ];
                }),
            );
            return Object.fromEntries(hypothesesInfo);
        },
        enabled,
        meta: {
            useGlobalOnError: true,
        },
    });
};
